// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { DealerModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@keller/App/Components/Notify";
import { CustomAppState } from "@keller/App/Store/Reducers";
import { getAllLocationsDataView } from "@keller/App/Store/Selectors/AllLocationsSelectors";
import AllLocationsFilterForm from "@keller/App/Widgets/AllLocations/AllLocationsFilterForm";
import DealerLines from "@keller/App/Widgets/AllLocations/DealerLines";
import { parseBoolean } from "@keller/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    return {
        allLocationsDataView: getAllLocationsDataView(state),
        industriesDataView: state.xDealers.industriesDataView,
        locationDetailsPageLink: getPageLinkByPageType(state, "LocationFinderPage")?.url,
    };
};

type Props = ReturnType<typeof mapStateToProps>;

export interface AllLocationsStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const allLocationsStyles: AllLocationsStyles = {
    centeringWrapper: {
        css: css`
            height: 300px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const styles = allLocationsStyles;
const AllLocations: FC<Props> = ({ allLocationsDataView, industriesDataView, locationDetailsPageLink }) => {
    const [filteredDealers, setFilteredDealers] = useState<DealerModel[] | undefined | null>(null);

    const updateFilteredDealers = (dealers: DealerModel[] | undefined) => {
        setFilteredDealers(dealers);
    };

    if (allLocationsDataView.isLoading || industriesDataView.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (allLocationsDataView.failedMessage) {
        return <Notify type="danger">{allLocationsDataView.failedMessage}</Notify>;
    }

    return (
        <StyledWrapper data-test-selector="All-Locations">
            <AllLocationsFilterForm
                dealers={allLocationsDataView.allLocations?.dealers}
                industries={industriesDataView.industries}
                updateFilteredDealers={updateFilteredDealers}
                hasShowroomLocations={parseBoolean(allLocationsDataView.allLocations?.properties.hasShowroomLocations)}
            />
            <DealerLines
                dealers={filteredDealers === null ? allLocationsDataView.allLocations?.dealers : filteredDealers}
                defaultDistanceUnitOfMeasure={allLocationsDataView.allLocations?.distanceUnitOfMeasure}
                locationDetailsPageLink={locationDetailsPageLink}
            />
        </StyledWrapper>
    );
};

const allLocationsWidget: WidgetModule = {
    component: connect(mapStateToProps)(AllLocations),
    definition: {
        group: "Location Finder",
    },
};

export default allLocationsWidget;
